import { ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, Router } from '@angular/router';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { SocialFeedService } from '../social-feed/services/social-feed.service';
import { SocialHubService } from '../social-feed/services/social-hub.service';
import { AutoPostField, SocialAuthor, SocialPost, SocialProfile } from '../social-feed/models/social-feed.model';

type ProfileTab = 'posts' | 'followers' | 'following' | 'settings';

/**
 * Someone's social page: header (follow / message / block, or editing your
 * own handle and bio), then Posts, Followers, Following and, on your own
 * page, Settings. The tab is kept in the address.
 */
@Component({
  selector: 'app-social-profile',
  templateUrl: './social-profile.component.html',
  styleUrls: ['./social-profile.component.scss'],
  standalone: false,
})
export class SocialProfileComponent implements OnInit {
  userId = 0;
  myId: number | null = null;
  profile: SocialProfile | null = null;
  loading = true;
  notFound = false;
  tab: ProfileTab = 'posts';

  posts: SocialPost[] = [];
  postsLoading = false;
  postsError = false;
  page = 1;
  hasMore = false;
  loadingMore = false;
  highlightId: number | null = null;
  /** Bumped whenever the list is reset, so a slow older answer can't land afterwards. */
  private seq = 0;

  people: SocialAuthor[] = [];
  peopleLoading = false;

  followBusy = false;
  blockBusy = false;
  confirmingBlock = false;

  editingBio = false;
  bioDraft = '';
  savingBio = false;
  editingHandle = false;
  handleDraft = '';
  handleError = '';
  savingHandle = false;

  readonly autoPostOptions: { field: AutoPostField; label: string; hint: string }[] = [
    { field: 'auto_post_map_created', label: 'When I create a map', hint: 'Posts "New map created" to your followers.' },
    { field: 'auto_post_game_created', label: 'When I create a game', hint: 'Posts "New game created" to your followers.' },
    { field: 'auto_post_joined', label: 'When I join a map or game', hint: 'Posts that you joined, in that map or game group.' },
  ];
  savingSetting: AutoPostField | null = null;

  constructor(
    private social: SocialFeedService,
    private hub: SocialHubService,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
    currentUser: CurrentUserService
  ) {
    currentUser.currentUser.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((u) => (this.myId = u?.id ?? null));
  }

  get isMe(): boolean {
    return this.profile?.author.id === this.myId;
  }

  ngOnInit(): void {
    this.highlightId = Number(this.route.snapshot.queryParamMap.get('post')) || null;
    // The same component is reused when going from one profile to another.
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const id = Number(params.get('userId'));
      if (id === this.userId) return;
      this.userId = id;
      const tab = this.route.snapshot.queryParamMap.get('tab') as ProfileTab | null;
      this.tab = tab && ['followers', 'following', 'settings'].includes(tab) ? tab : 'posts';
      this.loadProfile();
    });
  }

  loadProfile(): void {
    this.loading = true;
    this.notFound = false;
    this.profile = null;
    this.posts = [];
    this.people = [];
    this.social.getProfileByUserId(this.userId).subscribe({
      next: (profile) => {
        this.profile = profile;
        this.loading = false;
        if (this.tab === 'settings' && !this.isMe) this.tab = 'posts';
        this.selectTab(this.tab, false);
        this.cdr.markForCheck();
      },
      error: () => {
        this.notFound = true;
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  selectTab(tab: ProfileTab, updateUrl = true): void {
    this.tab = tab;
    if (updateUrl) {
      this.router.navigate([], { queryParams: { tab: tab === 'posts' ? null : tab }, replaceUrl: true });
    }
    if (tab === 'posts') this.loadPosts();
    if (tab === 'followers' || tab === 'following') this.loadPeople(tab);
  }

  // ---- posts --------------------------------------------------------------------

  loadPosts(): void {
    if (this.profile?.is_blocked) {
      this.posts = [];
      return;
    }
    this.postsLoading = true;
    this.postsError = false;
    const seq = ++this.seq;
    this.social.getFeed({ author: this.userId }).subscribe({
      next: (page) => {
        if (seq !== this.seq) return;
        this.posts = page.results;
        this.page = 1;
        this.hasMore = !!page.next;
        this.postsLoading = false;
        this.cdr.markForCheck();
        this.scrollToHighlight();
      },
      error: () => {
        if (seq !== this.seq) return;
        this.postsLoading = false;
        this.postsError = true;
        this.cdr.markForCheck();
      },
    });
  }

  loadMore(): void {
    if (this.loadingMore || !this.hasMore) return;
    this.loadingMore = true;
    const seq = this.seq;
    const next = this.page + 1;
    this.social.getFeed({ author: this.userId, page: next }).subscribe({
      next: (page) => {
        this.loadingMore = false;
        if (seq !== this.seq) return;
        const have = new Set(this.posts.map((p) => p.id));
        this.posts = [...this.posts, ...page.results.filter((p) => !have.has(p.id))];
        this.page = next;
        this.hasMore = !!page.next;
        this.cdr.markForCheck();
      },
      error: () => {
        this.loadingMore = false;
        this.cdr.markForCheck();
      },
    });
  }

  onRemoved(id: number): void {
    this.posts = this.posts.filter((p) => p.id !== id);
  }

  private scrollToHighlight(): void {
    const id = this.highlightId;
    if (!id) return;
    setTimeout(() => {
      document.getElementById('post-' + id)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        this.highlightId = null;
        this.cdr.markForCheck();
      }, 2500);
    }, 50);
  }

  // ---- followers / following -------------------------------------------------

  loadPeople(kind: 'followers' | 'following'): void {
    this.peopleLoading = true;
    this.people = [];
    (kind === 'followers' ? this.social.getFollowers(this.userId) : this.social.getFollowing(this.userId)).subscribe({
      next: (page) => {
        if (this.tab !== kind) return;
        this.people = page.results;
        this.peopleLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.peopleLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  openProfile(id: number): void {
    this.router.navigate(['/social-profile', id]);
  }

  // ---- actions ------------------------------------------------------------------

  toggleFollow(): void {
    if (!this.profile || this.followBusy) return;
    this.followBusy = true;
    const call = this.profile.is_following ? this.social.unfollowUser(this.userId) : this.social.followUser(this.userId);
    call.subscribe({
      next: (profile) => {
        this.profile = profile;
        this.followBusy = false;
        this.hub.refreshProfile();
        this.cdr.markForCheck();
      },
      error: () => {
        this.followBusy = false;
        this.cdr.markForCheck();
      },
    });
  }

  message(): void {
    this.router.navigate(['/social-messages', this.userId]);
  }

  confirmBlock(): void {
    if (this.blockBusy) return;
    this.blockBusy = true;
    this.social.blockUser(this.userId).subscribe({
      next: (profile) => {
        this.profile = profile;
        this.posts = [];
        this.seq++;
        this.blockBusy = false;
        this.confirmingBlock = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.blockBusy = false;
        this.cdr.markForCheck();
      },
    });
  }

  unblock(): void {
    if (this.blockBusy) return;
    this.blockBusy = true;
    this.social.unblockUser(this.userId).subscribe({
      next: (profile) => {
        this.profile = profile;
        this.blockBusy = false;
        this.loadPosts();
        this.cdr.markForCheck();
      },
      error: () => {
        this.blockBusy = false;
        this.cdr.markForCheck();
      },
    });
  }

  // ---- editing your own -----------------------------------------------------------

  startEditBio(): void {
    this.bioDraft = this.profile?.bio || '';
    this.editingBio = true;
  }

  saveBio(): void {
    if (this.savingBio) return;
    this.savingBio = true;
    this.social.updateMyProfile(this.bioDraft.trim()).subscribe({
      next: (profile) => {
        this.profile = profile;
        this.editingBio = false;
        this.savingBio = false;
        this.hub.refreshProfile();
        this.cdr.markForCheck();
      },
      error: () => {
        this.savingBio = false;
        this.cdr.markForCheck();
      },
    });
  }

  startEditHandle(): void {
    this.handleDraft = this.profile?.handle ?? '';
    this.handleError = '';
    this.editingHandle = true;
  }

  saveHandle(): void {
    if (this.savingHandle) return;
    const value = this.handleDraft.trim().replace(/^@/, '').toLowerCase();
    if (!/^[a-z0-9_]{3,30}$/.test(value)) {
      this.handleError = '3–30 characters: lowercase letters, numbers and underscores.';
      return;
    }
    if (value === this.profile?.handle) {
      this.editingHandle = false;
      return;
    }
    this.savingHandle = true;
    this.handleError = '';
    this.social.updateMyHandle(value).subscribe({
      next: (profile) => {
        this.profile = profile;
        this.editingHandle = false;
        this.savingHandle = false;
        this.hub.refreshProfile();
        this.cdr.markForCheck();
      },
      error: (err: HttpErrorResponse) => {
        this.handleError = err?.error?.error || err?.message || "Couldn't save your handle. Please try again.";
        this.savingHandle = false;
        this.cdr.markForCheck();
      },
    });
  }

  toggleAutoPost(field: AutoPostField, value: boolean): void {
    if (!this.profile || this.savingSetting) return;
    const previous = this.profile[field];
    this.profile[field] = value;
    this.savingSetting = field;
    this.social.updateMySettings({ [field]: value }).subscribe({
      next: (profile) => {
        this.profile = profile;
        this.savingSetting = null;
        this.cdr.markForCheck();
      },
      error: () => {
        if (this.profile) this.profile[field] = previous;
        this.savingSetting = null;
        this.cdr.markForCheck();
      },
    });
  }

  imageUrl(path: string | null | undefined): string {
    return this.social.resolveImageUrl(path);
  }

  initials(name: string | undefined | null): string {
    return (name || '').split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  }
}
